import React, { useState } from 'react';
import { UsersIcon, PlusIcon, EditIcon, UserCheckIcon, UserXIcon, ShieldIcon, UserIcon, AlertCircleIcon, Trash2Icon, LockIcon, SearchIcon } from 'lucide-react';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { StatsCard } from '../components/ui/StatsCard';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { User } from '../types';
export function UsersPage() {
  const {
    users,
    roles,
    addUser,
    updateUser,
    deleteUser
  } = useApp();
  const {
    addActivityLog
  } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    password: '',
    rolId: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const resetForm = () => {
    setFormData({
      nombre: '',
      email: '',
      password: '',
      rolId: roles.length > 0 ? roles[0].id : ''
    });
    setErrors({});
    setEditingUser(null);
    setSubmitError('');
  };
  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };
  const openEditModal = (user: User) => {
    setEditingUser(user);
    setFormData({
      nombre: user.nombre,
      email: user.email,
      password: '',
      rolId: user.rolId
    });
    setSubmitError('');
    setErrors({});
    setIsModalOpen(true);
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es requerido';
    if (!formData.rolId) newErrors.rolId = 'El rol es requerido';
    if (!editingUser && !formData.password) newErrors.password = 'La contraseña es requerida para nuevos usuarios';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    setSubmitError('');
    try {
      if (editingUser) {
        const updates: Partial<User> = {
          nombre: formData.nombre,
          email: formData.email,
          rolId: formData.rolId
        };
        if (formData.password) updates.password = formData.password;
        const result = await updateUser(editingUser.id, updates);
        if (!result.success) throw new Error(result.error);
        addActivityLog('Editó usuario', `Usuario: ${formData.nombre} | Rol: ${roles.find(r => r.id === formData.rolId)?.nombre || ''}`);
      } else {
        const result = await addUser({
          nombre: formData.nombre,
          email: formData.email,
          password: formData.password,
          rolId: formData.rolId,
          activo: true
        });
        if (!result.success) throw new Error(result.error);
        addActivityLog('Creó usuario', `Usuario: ${formData.nombre} | Rol: ${roles.find(r => r.id === formData.rolId)?.nombre || ''}`);
      }
      setIsModalOpen(false);
      resetForm();
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Error al guardar usuario');
    } finally {
      setIsSubmitting(false);
    }
  };
  const toggleUserStatus = async (user: User) => {
    await updateUser(user.id, {
      activo: !user.activo
    });
    addActivityLog(user.activo ? 'Desactivó usuario' : 'Activó usuario', `Usuario: ${user.nombre}`);
  };
  const handleDelete = async (user: User) => {
    if (!confirm(`¿Eliminar a ${user.nombre}? Esta acción no se puede deshacer.`)) return;
    await deleteUser(user.id);
    addActivityLog('Eliminó usuario', `Usuario: ${user.nombre}`);
  };
  const roleOptions = roles.map(r => ({
    value: r.id,
    label: r.nombre
  }));
  const activeUsers = users.filter(u => u.activo);
  const inactiveUsers = users.filter(u => !u.activo);
  const filteredUsers = users.filter(u => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return u.nombre.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || u.rolNombre.toLowerCase().includes(q);
  });
  const getUserAvatar = (userId: string): string => {
    try {
      return localStorage.getItem(`ventastrack_avatar_${userId}`) || '';
    } catch {
      return '';
    }
  };
  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  return <div className="space-y-4 sm:space-y-6" data-id="element-4723" data-mp="3618oi6mxtzku">
      <PageHeader title="Gestión de Usuarios" subtitle="Administración de vendedores y personal" icon={<UsersIcon className="w-6 h-6" data-id="element-4725" data-mp="2ygelcpzxylyz" />} data-id="element-4724" data-mp="35zxkq6thsrtq">
        
        <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-4727" data-mp="2ti3bwgdk6dha" />} onClick={openCreateModal} data-id="element-4726" data-mp="2timsntrh2vtq">
          
          Nuevo Usuario
        </Button>
      </PageHeader>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 animate-stagger-1" data-id="element-4728" data-mp="2olmo5na9u14q">
        <StatsCard title="Total Usuarios" value={users.length} icon={<UsersIcon className="w-5 h-5" data-id="element-4730" data-mp="2fjdgd56e0lv1" />} variant="primary" data-id="element-4729" data-mp="2jjx97wjxh3bd" />
        
        <StatsCard title="Usuarios Activos" value={activeUsers.length} icon={<UserCheckIcon className="w-5 h-5" data-id="element-4732" data-mp="2aqk661s5glbk" />} variant="success" data-id="element-4731" data-mp="2fkoeoph2d1er" />
        
        <StatsCard title="Usuarios Inactivos" value={inactiveUsers.length} icon={<UserXIcon className="w-5 h-5" data-id="element-4734" data-mp="20u7e18c96fix" />} variant="danger" data-id="element-4733" data-mp="25u3jzd4igh14" />
        
      </div>

      {/* Search */}
      <div className="relative max-w-md animate-stagger-2" data-id="element-4735" data-mp="1vve2hlgj0yle">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-4736" data-mp="1vwyv35ja67gm" />
        <Input placeholder="Buscar usuarios..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-id="element-4737" data-mp="1rvdn9yllsjda" />
        
      </div>

      {/* Table */}
      <Card padding="none" className="animate-stagger-3" data-id="element-4738" data-mp="1m8tmliihbw9g">
        <div className="px-4 py-4 sm:px-6 sm:py-5 border-b border-slate-100" data-id="element-4739" data-mp="1880q4s8mar3p">
          <CardHeader title="Lista de Usuarios" data-id="element-4740" data-mp="1d6kcuuv1c6x4" />
        </div>
        <div className="divide-y divide-slate-100" data-id="element-4741" data-mp="1d73ueagrwtam">
          {filteredUsers.length === 0 ? <div className="p-8 sm:p-16 text-center animate-fade-in" data-id="element-4742" data-mp="20x7qlwrmtqrl">
              <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-4743" data-mp="24y9oa95whurg">
                <UsersIcon className="w-7 h-7 sm:w-8 sm:h-8 text-slate-400" data-id="element-4744" data-mp="1r5k1i2ijslta" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-1" data-id="element-4745" data-mp="1w4el36ocaeeg">
                No hay usuarios
              </h3>
              <p className="text-slate-500 text-sm" data-id="element-4746" data-mp="2jsxrthrqqj4f">
                Crea el primer usuario con el botón de arriba
              </p>
            </div> : filteredUsers.map(user => <div key={user.id} className="px-4 py-3 sm:px-6 sm:py-4 hover:bg-slate-50/50 transition-colors" data-id="element-4747" data-mp="2xz8241oto7zy">
            
                <div className="flex items-center justify-between" data-id="element-4748" data-mp="2twuypjxjsm6c">
                  <div className="flex items-center gap-3 sm:gap-4 min-w-0" data-id="element-4749" data-mp="37p12e6jil0ow">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0" style={{
                backgroundColor: user.avatarUrl ? 'transparent' : '#eef2fc'
              }} data-id="element-4750" data-mp="32qr9y3p6ce6z">
                  
                      {user.avatarUrl ? <img src={user.avatarUrl} alt={user.nombre} className="w-full h-full object-cover" data-id="element-4751" data-mp="2ozup4z1hp1ah" /> : <span className="text-xs sm:text-sm font-bold" style={{
                  color: '#2952C4'
                }} data-id="element-4752" data-mp="2oxi2w9op84rm">
                    
                          {getInitials(user.nombre)}
                        </span>}
                    </div>
                    <div className="min-w-0" data-id="element-4753" data-mp="3vfexk5xebfnp">
                      <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap" data-id="element-4754" data-mp="1tqr42rkehk2r">
                        <h4 className="font-semibold text-slate-900 font-heading text-sm sm:text-base truncate" data-id="element-4755" data-mp="1toxyzgpfr7wv">
                          {user.nombre}
                        </h4>
                        <Badge variant={user.activo ? 'success' : 'danger'} data-id="element-4756" data-mp="22q4epnk9gep2">
                          {user.activo ? 'Activo' : 'Inactivo'}
                        </Badge>
                        <Badge variant={user.rolNombre === 'Administrador' ? 'info' : 'default'} data-id="element-4757" data-mp="2cggyveo5y5uo">
                      
                          {user.rolNombre}
                        </Badge>
                      </div>
                      <p className="text-[11px] sm:text-xs text-slate-400 mt-0.5 truncate" data-id="element-4758" data-mp="2liq71xvyln4w">
                        {user.email} • Creado:{' '}
                        {new Date(user.fechaCreacion).toLocaleDateString('es-DO')}
                      </p>
                    </div>
                  </div>
                  {/* Mobile action buttons (icon-only) */}
                  <div className="flex items-center gap-1 sm:hidden flex-shrink-0 ml-2" data-id="element-4759" data-mp="ojrfoegm8c7h">
                    <button onClick={() => openEditModal(user)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-4760" data-mp="jshoh9ff50th">
                  
                      <EditIcon className="w-4 h-4" data-id="element-4761" data-mp="9uv80iih117e" />
                    </button>
                    <button onClick={() => toggleUserStatus(user)} className={`p-1.5 rounded-lg transition-colors ${user.activo ? 'text-slate-400 hover:text-rose-600 hover:bg-rose-50' : 'text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50'}`} data-id="element-4762" data-mp="9sqvxdaf4vw1">
                  
                      {user.activo ? <UserXIcon className="w-4 h-4" data-id="element-4763" data-mp="xm8ysk0z4oq9" /> : <UserCheckIcon className="w-4 h-4" data-id="element-4764" data-mp="tj3x2jopwr2o" />}
                    </button>
                    <button onClick={() => handleDelete(user)} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-4765" data-mp="3ia551c7i7g8b">
                  
                      <Trash2Icon className="w-4 h-4" data-id="element-4766" data-mp="3eb619uxidpig" />
                    </button>
                  </div>
                  {/* Desktop action buttons */}
                  <div className="hidden sm:flex items-center gap-2 flex-shrink-0 ml-4" data-id="element-4767" data-mp="2e3qaykqzqflb">
                    <Button variant="ghost" size="sm" onClick={() => openEditModal(user)} data-id="element-4768" data-mp="2ruvevz1e5u1g">
                  
                      <EditIcon className="w-4 h-4" data-id="element-4769" data-mp="2n2brkacwjkjg" />
                    </Button>
                    <Button variant={user.activo ? 'danger' : 'success'} size="sm" onClick={() => toggleUserStatus(user)} data-id="element-4770" data-mp="31r852mxq9prg">
                  
                      {user.activo ? 'Desactivar' : 'Activar'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(user)} data-id="element-4771" data-mp="3arwmlrmby893">
                  
                      <Trash2Icon className="w-4 h-4 text-rose-400" data-id="element-4772" data-mp="35ribg0pfmi6u" />
                    </Button>
                  </div>
                </div>
              </div>)}
        </div>
      </Card>

      {/* Modal */}
      <Modal isOpen={isModalOpen} onClose={() => {
      setIsModalOpen(false);
      resetForm();
    }} title={editingUser ? 'Editar Usuario' : 'Nuevo Usuario'} data-id="element-4773" data-mp="1bhy8l3r96kxt">
        
        <ModalBody data-id="element-4774" data-mp="3s1ifrauttw38">
          <div className="space-y-4" data-id="element-4775" data-mp="3s44imx6rvnf4">
            {submitError && <div className="flex items-start gap-3 p-3 bg-rose-50 border border-rose-200 rounded-xl" data-id="element-4776" data-mp="3e9kd9ivmeyg1">
                <AlertCircleIcon className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" data-id="element-4777" data-mp="3ib5n0vcuywm0" />
                <p className="text-sm text-rose-700" data-id="element-4778" data-mp="34bebd19tdsb6">{submitError}</p>
              </div>}
            <Input label="Nombre Completo" placeholder="Ej. Juan Pérez" value={formData.nombre} onChange={e => setFormData({
            ...formData,
            nombre: e.target.value
          })} error={errors.nombre} data-id="element-4779" data-mp="39c235p5vad6g" />
            
            <Input label="Usuario o correo (para iniciar sesión)" placeholder="juan  o  juan@empresa.com" type="text" autoComplete="off" value={formData.email} onChange={e => setFormData({
            ...formData,
            email: e.target.value
          })} data-id="element-4780" data-mp="2zduvxy1dr9hn" />
            
            <Select label="Rol" options={roleOptions} value={formData.rolId} onChange={e => setFormData({
            ...formData,
            rolId: e.target.value
          })} error={errors.rolId} data-id="element-4781" data-mp="26jzhrhzlnhd5" />
            
            <Input label={editingUser ? 'Nueva Contraseña (dejar en blanco para mantener)' : 'Contraseña'} type="password" placeholder="••••••••" value={formData.password} onChange={e => setFormData({
            ...formData,
            password: e.target.value
          })} error={errors.password} icon={<LockIcon className="w-4 h-4 text-slate-400" data-id="element-4783" data-mp="2l940d0ab3nls" />} data-id="element-4782" data-mp="1xirxw4xtyj2l" />
            
          </div>
        </ModalBody>
        <ModalFooter data-id="element-4784" data-mp="2gfje25i2si2f">
          <Button variant="secondary" onClick={() => {
          setIsModalOpen(false);
          resetForm();
        }} data-id="element-4785" data-mp="2gergip4x4a9i">
            
            Cancelar
          </Button>
          <Button onClick={handleSubmit} isLoading={isSubmitting} data-id="element-4786" data-mp="38yyctgqpra5i">
            {editingUser ? 'Guardar Cambios' : 'Crear Usuario'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>;
}